import { useState } from 'react'
import { useToast } from './Toasts'

/**
 * Shows a password once, and says so.
 *
 * It is not stored anywhere it can be read back. Nothing in the product can
 * show it again, and nobody, including us, can look it up. Closing this panel
 * is the last time it exists outside whatever the person pastes it into.
 *
 * That is deliberate rather than a gap. A password that can be looked up later
 * is a password that can be looked up by somebody else later.
 */
export function PasswordOnce({
  email,
  password,
  onDone,
  heading = 'Their password',
}: {
  email: string
  password: string
  onDone: () => void
  heading?: string
}) {
  const toast = useToast()
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
      toast.ok('Password copied.')
    } catch {
      // Some browsers refuse without a secure page. The password is on screen
      // either way, so this is a smaller problem than it looks.
      toast.fail('Copying is blocked here. Select the password and copy it.')
    }
  }

  return (
    <div className="card card--pad onceonly" role="alertdialog" aria-labelledby="pw-title">
      <h2 id="pw-title" className="onceonly__title">{heading}</h2>
      <p className="onceonly__body">
        Send this to <b>{email}</b>. It is shown once. When this panel closes there
        is no way to see it again, so copy it now. If it is lost, set a new one.
      </p>

      <div className="onceonly__value">
        <code>{password}</code>
        <button type="button" className="btn btn--sm" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <p className="onceonly__note">
        It works for three days. The first time they sign in they are asked to choose
        their own password, and this one stops working straight away.
      </p>

      <div className="actions">
        <button type="button" className="btn btn--primary" onClick={onDone}>
          I have copied it
        </button>
      </div>
    </div>
  )
}
